import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import type { BusinessOpportunity, BusinessOpportunityStatus } from '../types';
import { newId } from '../utils/id';
import { auth, db } from './firebase';
import {
  COLLECTION_BUSINESS_OPPORTUNITIES,
  COLLECTION_OPPORTUNITY_CHAT_MESSAGES,
  ownedItemDocId,
} from './firestorePaths';

export type BusinessOpportunityInput = {
  id?: string;
  name: string;
  industry?: string;
  ideaSummary: string;
  status?: BusinessOpportunityStatus;
  ideaIntelligence?: BusinessOpportunity['ideaIntelligence'];
  refinements?: BusinessOpportunity['refinements'];
  iterations?: BusinessOpportunity['iterations'];
  feedback?: BusinessOpportunity['feedback'];
  pnl?: BusinessOpportunity['pnl'];
  roadmap?: BusinessOpportunity['roadmap'];
  promotedCaseId?: string;
  existing?: Pick<BusinessOpportunity, 'createdAt' | 'status' | 'refinements' | 'iterations' | 'feedback' | 'ideaIntelligence' | 'pnl' | 'roadmap' | 'promotedCaseId'>;
};

function stripUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value
      .map((entry) => stripUndefinedDeep(entry))
      .filter((entry) => entry !== undefined);
  }
  const next: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    const stripped = stripUndefinedDeep(val);
    if (stripped !== undefined) next[key] = stripped;
  }
  return next;
}

function resolveAuthUserId(fallbackUserId: string): string {
  const authUid = auth.currentUser?.uid?.trim();
  if (authUid) return authUid;
  const trimmed = fallbackUserId.trim();
  if (trimmed) return trimmed;
  throw new Error('Sign in to save business opportunities.');
}

export async function saveBusinessOpportunity(
  userId: string,
  input: BusinessOpportunityInput
): Promise<BusinessOpportunity> {
  const ownerId = resolveAuthUserId(userId);
  const name = input.name.trim();
  if (!name) {
    throw new Error('Opportunity name is required.');
  }

  const ideaSummary = input.ideaSummary.trim();
  if (!ideaSummary) {
    throw new Error('Describe your idea before saving.');
  }

  const now = new Date().toISOString();
  const id = input.id?.trim() || newId();
  const docRef = doc(db, COLLECTION_BUSINESS_OPPORTUNITIES, ownedItemDocId(ownerId, id));

  const opportunity: BusinessOpportunity = {
    id,
    name,
    industry: input.industry?.trim() || undefined,
    ideaSummary,
    status: input.status ?? input.existing?.status ?? 'draft',
    ideaIntelligence: input.ideaIntelligence ?? input.existing?.ideaIntelligence,
    refinements: input.refinements ?? input.existing?.refinements,
    iterations: input.iterations ?? input.existing?.iterations,
    feedback: input.feedback ?? input.existing?.feedback,
    pnl: input.pnl ?? input.existing?.pnl,
    roadmap: input.roadmap ?? input.existing?.roadmap,
    promotedCaseId: input.promotedCaseId ?? input.existing?.promotedCaseId,
    createdAt: input.existing?.createdAt ?? now,
    updatedAt: now,
  };

  await setDoc(
    docRef,
    stripUndefinedDeep({ userId: ownerId, ...opportunity }) as Record<string, unknown>,
    { merge: Boolean(input.existing) }
  );

  return opportunity;
}

const FIRESTORE_BATCH_LIMIT = 500;

async function deleteOpportunityChatMessages(ownerId: string, opportunityId: string): Promise<void> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION_OPPORTUNITY_CHAT_MESSAGES),
      where('userId', '==', ownerId),
      where('opportunityId', '==', opportunityId)
    )
  );

  for (let i = 0; i < snap.docs.length; i += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const messageDoc of snap.docs.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
      batch.delete(messageDoc.ref);
    }
    await batch.commit();
  }
}

export async function deleteBusinessOpportunity(userId: string, id: string): Promise<void> {
  const ownerId = resolveAuthUserId(userId);
  const opportunityId = id.trim();
  const docRef = doc(db, COLLECTION_BUSINESS_OPPORTUNITIES, ownedItemDocId(ownerId, opportunityId));
  await deleteOpportunityChatMessages(ownerId, opportunityId);
  await deleteDoc(docRef);
}
